import { RegressionOutputSuccess } from '@aloft/toolkit-shared';
import { Flex, InfoIcon, SimpleSelect } from '@aloft/toolkit-sui';

import { useState } from 'react';

import { RegressionScatterGraph } from '../../../components/graphs/regression/RegressionScatterGraph.tsx';
import { RegressionScatterPoint } from '../../../components/graphs/regression/types.ts';
import { listingsToScatterData } from '../../../components/graphs/regression/utils.ts';

const GRAPH_HEIGHT = 300;

type RightGraphOption = {
  label: string;
  xKey: keyof RegressionScatterPoint;
  xLabel: string;
};

const RIGHT_GRAPH_OPTIONS: RightGraphOption[] = [
  { label: 'GLA vs. Sale Price', xKey: 'gla', xLabel: 'GLA (sqft)' },
  { label: 'Actual Age vs. Sale Price', xKey: 'actualAge', xLabel: 'Actual Age (yrs)' },
  { label: 'Bedroom Count vs. Sale Price', xKey: 'bedrooms', xLabel: 'Bedroom Count' },
  { label: 'Full Bathrooms vs. Sale Price', xKey: 'fullBaths', xLabel: 'Full Bathrooms' },
  { label: 'Half Bathrooms vs. Sale Price', xKey: 'halfBaths', xLabel: 'Half Bathrooms' },
];

type RegressionModalGraphSectionProps = {
  output: RegressionOutputSuccess;
};

export const RegressionModalGraphSection = ({ output }: RegressionModalGraphSectionProps) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState('0');

  const data = listingsToScatterData(output.listings, output);

  const selectedOption =
    RIGHT_GRAPH_OPTIONS[Number(selectedOptionIndex)] ?? RIGHT_GRAPH_OPTIONS[0]!;

  return (
    <Flex flexDirection="column" gap="0.75rem">
      <Flex gap="1rem" alignItems="stretch">
        <Flex flexDirection="column" flex={1}>
          <RegressionScatterGraph
            data={data}
            xKey="predictedPrice"
            yKey="actualPrice"
            xLabel="Predicted Sale Price"
            yLabel="Actual Sale Price"
            disableAnimations={false}
            height={GRAPH_HEIGHT}
            showBestFitLine
          />
        </Flex>
        <Flex
          flexDirection="column"
          gap="0.5rem"
          flex={1}
          backgroundColor="graphBackground"
          borderRadius="8px"
          padding="0.75rem"
        >
          <Flex alignItems="center" gap="0.5rem">
            <SimpleSelect
              value={selectedOptionIndex}
              onChange={(e) => setSelectedOptionIndex(e.target.value)}
            >
              {RIGHT_GRAPH_OPTIONS.map((option, index) => (
                <option key={option.label} value={String(index)}>
                  {option.label}
                </option>
              ))}
            </SimpleSelect>
            <InfoIcon tooltipContent="Multivariate regression is necessary to account for the various characteristics that simultaneously influence a property's value." />
          </Flex>
          <RegressionScatterGraph
            data={data}
            xKey={selectedOption.xKey}
            yKey="actualPrice"
            xLabel={selectedOption.xLabel}
            yLabel="Sale Price"
            disableAnimations={false}
            height={GRAPH_HEIGHT}
            bare
          />
        </Flex>
      </Flex>
    </Flex>
  );
};
